import React from 'react';
import {Chip, Tooltip} from '@mui/material';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import {
    eeiPriorityChipColor,
    formatEeiPriorityLabel,
    normalizeEeiPriority,
} from '../../../firebase/firestore/eei';

const PRIORITY_ICONS = {
    High: KeyboardDoubleArrowUpIcon,
    Medium: DragHandleIcon,
    Low: KeyboardDoubleArrowDownIcon,
};

export function PriorityLevelIcon({priority, fontSize = 14}) {
    const normalized = normalizeEeiPriority(priority);
    const Icon = PRIORITY_ICONS[normalized] || PRIORITY_ICONS.Medium;
    return <Icon sx={{fontSize, display: 'block'}} aria-hidden/>;
}

/**
 * Priority indicator with MUI icons and optional label chip.
 */
export default function PriorityIndicator({
    priority,
    size = 'small',
    showLabel = true,
    variant = 'outlined',
    sx,
    ...chipProps
}) {
    const normalized = normalizeEeiPriority(priority);
    const labelText = formatEeiPriorityLabel(normalized);
    const iconSize = size === 'small' ? 14 : 16;
    const icon = <PriorityLevelIcon priority={normalized} fontSize={iconSize}/>;

    const chip = (
        <Chip
            size={size}
            variant={variant}
            color={eeiPriorityChipColor(normalized)}
            icon={icon}
            label={showLabel ? labelText : undefined}
            aria-label={labelText}
            sx={{
                height: size === 'small' ? 20 : 24,
                fontWeight: 700,
                '& .MuiChip-icon': {
                    ml: showLabel ? 0.5 : 0,
                    mr: showLabel ? -0.25 : 0,
                    color: 'inherit',
                },
                '& .MuiChip-label': showLabel ? {px: 0.75} : {display: 'none', p: 0},
                ...(!showLabel && {
                    width: 22,
                    minWidth: 22,
                    '& .MuiChip-icon': {
                        mx: 'auto',
                        ml: 0,
                        mr: 0,
                    },
                }),
                ...sx,
            }}
            {...chipProps}
        />
    );

    if (showLabel) {
        return chip;
    }

    return (
        <Tooltip title={labelText} arrow>
            {chip}
        </Tooltip>
    );
}
